import type { StageArtKind } from '../../features/studio/film/StageArt';

export type LoaderModuleId =
  | 'home'
  | 'story'
  | 'cast'
  | 'scene'
  | 'production'
  | 'editor'
  | 'finish'
  | 'marketing'
  | 'vfx'
  | 'saas'
  | 'upscale'
  | 'talk'
  | 'crew'
  | 'jobs'
  | 'cost'
  | 'plans'
  | 'reviews'
  | 'scheduler'
  | 'settings'
  | 'guest-review'
  | 'canvas'
  | 'board'
  | 'media';

export interface LoaderModule {
  art: StageArtKind;
  eyebrow: string;
  title: string;
  hints: string[];
}

export const LOADER_MODULES: Record<LoaderModuleId, LoaderModule> = {
  home: {
    art: 'slate',
    eyebrow: 'Productions',
    title: 'Opening your productions',
    hints: ['Every production keeps its own story, cast, shots and cut.', 'Films, campaigns, VFX, SaaS walkthroughs and upscales share one floor.'],
  },
  story: {
    art: 'story',
    eyebrow: 'Film · Story & Script',
    title: 'Opening the writers’ room',
    hints: ['Cue develops the story and writes the screenplay.', 'Production anchors come from the approved screenplay.'],
  },
  cast: {
    art: 'cast',
    eyebrow: 'Film · Cast & Places',
    title: 'Lighting the casting stage',
    hints: ['Lock an identity and every shot stays consistent.', 'Cast, places and hero objects come from the locked screenplay.'],
  },
  scene: {
    art: 'scene',
    eyebrow: 'Film · Scene Direct',
    title: 'Laying the dolly track',
    hints: ['In a scene, [ and ] step between scenes.', 'Video unlocks once a shot’s still is locked.'],
  },
  production: {
    art: 'production',
    eyebrow: 'Film · Production',
    title: 'Threading the reels',
    hints: ['Stat tiles filter the board. Press again to clear.', 'Open a scene to direct or generate its video.'],
  },
  editor: {
    art: 'editor',
    eyebrow: 'Film · Edit',
    title: 'Loading the timeline',
    hints: ['Selected footage lands on the timeline.', 'Adjust timing, then export or render the cut.'],
  },
  finish: {
    art: 'finish',
    eyebrow: 'Film · Finish',
    title: 'Preparing the finish review',
    hints: ['Takes → QC → Assembly → Master → Final approvals.', 'Share an External Director Review when the cut is ready.'],
  },
  marketing: {
    art: 'campaign',
    eyebrow: 'Campaign',
    title: 'Briefing the agency',
    hints: ['Session and Campaign desk share one column. Drafts survive the switch.', 'On the Content tab, / focuses search.', 'Idea → Refs → Copy → Stills → Film → Ship.'],
  },
  vfx: {
    art: 'vfx',
    eyebrow: 'RAW 2.0 · VFX',
    title: 'Loading the plate',
    hints: ['Shape a new world with tracked mattes and Seedance edits.', 'Export MP4, ProRes or an EXR sequence with a delivery manifest.'],
  },
  saas: {
    art: 'saas',
    eyebrow: 'SaaS Studio',
    title: 'Rebuilding the screens',
    hints: ['Every detail sits on its own layer, so motion hits real controls.', 'Your current screen stays unchanged until Apply.'],
  },
  upscale: {
    art: 'upscale',
    eyebrow: 'Upscale',
    title: 'Sharpening the frame',
    hints: ['Upload footage, pick a resolution, confirm the spend.'],
  },
  talk: {
    art: 'talk',
    eyebrow: 'Talk',
    title: 'Warming up the booth',
    hints: ['Turn a script into a performance, take by take.'],
  },
  crew: {
    art: 'crew',
    eyebrow: 'Project crew',
    title: 'Assembling the crew',
    hints: ['Specialists join the session when the work needs them.', 'Custom skills stay with this project.'],
  },
  jobs: {
    art: 'jobs',
    eyebrow: 'Render queue',
    title: 'Reading the render queue',
    hints: ['Jobs refresh on their own every few seconds.', 'Resume reuses a provider request when one already exists.'],
  },
  cost: {
    art: 'cost',
    eyebrow: 'Cost',
    title: 'Balancing the ledger',
    hints: ['Spend is estimated before anything generates.'],
  },
  plans: {
    art: 'cost',
    eyebrow: 'Plans',
    title: 'Opening the price book',
    hints: ['Subscriptions carry the platform; credits meter the models.'],
  },
  scheduler: {
    art: 'jobs',
    eyebrow: 'Scheduler',
    title: 'Reading the studio queue',
    hints: ['Every long-running task across your productions, in one place.', 'Approve, run in parallel, or jump to the page that failed.'],
  },
  reviews: {
    art: 'review',
    eyebrow: 'Reviews',
    title: 'Opening the screening room',
    hints: ['Review banks gather every decision and note in one place.'],
  },
  settings: {
    art: 'settings',
    eyebrow: 'Settings',
    title: 'Loading studio settings',
    hints: ['Provider keys are encrypted at rest.'],
  },
  'guest-review': {
    art: 'review',
    eyebrow: 'Secure review',
    title: 'Opening secure review',
    hints: ['Watch the cut, pin notes, then approve or request changes.'],
  },
  canvas: {
    art: 'scene',
    eyebrow: 'Scene Direct',
    title: 'Loading 3D canvas',
    hints: [],
  },
  board: {
    art: 'crew',
    eyebrow: 'Canvas',
    title: 'Opening the board',
    hints: ['Drop live shot cards onto the board — they keep their current state.', 'Select a card to comment, react or assign it.'],
  },
  media: {
    art: 'slate',
    eyebrow: 'Media',
    title: 'Loading media',
    hints: [],
  },
};

const FILM_STAGE_MODULE: Record<string, LoaderModuleId> = {
  idea: 'story',
  world: 'cast',
  plan: 'scene',
  generate: 'production',
  edit: 'editor',
  review: 'finish',
};

const SAAS_STAGES = new Set(['brief', 'brand', 'screens', 'rebuild', 'storyboard', 'motion', 'audio']);

const DOMAIN_MODULE: Record<string, LoaderModuleId> = {
  marketing: 'marketing',
  vfx: 'vfx',
  saas: 'saas',
  upscale: 'upscale',
  talk: 'talk',
};

const KIND_KEY = 'frameon:project-domain:';

export function rememberProjectDomain(projectId: string, domain: string) {
  try {
    window.localStorage.setItem(KIND_KEY + projectId, domain);
  } catch {
    return;
  }
}

function recalledProjectDomain(projectId: string) {
  try {
    return window.localStorage.getItem(KIND_KEY + projectId) || '';
  } catch {
    return '';
  }
}

export type LoaderTarget = { kind: 'brand'; message: string } | { kind: 'module'; module: LoaderModuleId };

const BRAND_ROUTES: Record<string, string> = {
  '/signin': 'Opening sign in…',
  '/signup': 'Setting up your studio…',
  '/welcome': 'Preparing your welcome…',
  '/signout': 'Closing your session…',
  '/auth/callback': 'Signing you in…',
  '/invite': 'Opening your invitation…',
  '/viral': 'Loading Go Viral presets…',
  '/viral/admin': 'Opening the preset catalog…',
};

export function loaderTargetForPath(pathname: string): LoaderTarget {
  const path = pathname.replace(/\/+$/, '') || '/';
  if (BRAND_ROUTES[path]) return { kind: 'brand', message: BRAND_ROUTES[path] };
  if (path === '/') return { kind: 'brand', message: 'Opening your studio…' };
  if (path === '/review' || path === '/review.html') return { kind: 'module', module: 'guest-review' };
  if (path === '/generator') return { kind: 'module', module: 'talk' };
  if (path === '/cost') return { kind: 'module', module: 'cost' };
  if (path === '/pricing') return { kind: 'module', module: 'plans' };
  if (path === '/reviews') return { kind: 'module', module: 'reviews' };
  if (path === '/scheduler') return { kind: 'module', module: 'scheduler' };
  if (path === '/settings') return { kind: 'module', module: 'settings' };
  const project = /^\/projects\/([^/]+)(?:\/([^/]+))?/.exec(path);
  if (!project) return { kind: 'brand', message: 'Opening your studio…' };
  const [, projectId, section = 'idea'] = project;
  if (section === 'saas' || SAAS_STAGES.has(section)) return { kind: 'module', module: 'saas' };
  if (section === 'raw' || section === 'deliver') return { kind: 'module', module: 'vfx' };
  if (section === 'jobs') return { kind: 'module', module: 'jobs' };
  if (section === 'agents') return { kind: 'module', module: 'crew' };
  if (section === 'onboard') return { kind: 'module', module: 'marketing' };
  if (section === 'upscale') return { kind: 'module', module: 'upscale' };
  const remembered = DOMAIN_MODULE[recalledProjectDomain(decodeURIComponent(projectId))];
  if (remembered) return { kind: 'module', module: remembered };
  return { kind: 'module', module: FILM_STAGE_MODULE[section] || 'story' };
}
